import React, { useState, useContext } from "react"
import styled, { css } from "styled-components"
import { rem } from "polished"
import { StaticQuery, graphql } from "gatsby"
import { motion, AnimatePresence } from "framer-motion"
import Img from "gatsby-image"

import { LoadedContext } from "../loaded-context"

import Container from "../container"

import { ReactComponent as NavLogo } from "../../assets/images/supercontinental-logo.svg"

const Images = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: ${rem(500)};
  height: ${rem(500)};
  pointer-events: none;
`

const SingleImage = styled(motion.div)`
  &:nth-of-type(1) {
    position: absolute;
    width: 50%;
    top: 0;
    left: 0;

    @media ${props => props.theme.mobileDown} {
      width: 30%;
      left: 15%;
      top: 15%;
    }
  }

  &:nth-of-type(2) {
    position: absolute;
    width: 80%;
    top: 0;
    left: 20%;

    @media ${props => props.theme.mobileDown} {
      width: 50%;
      left: 30%;
    }
  }

  &:nth-of-type(3) {
    position: absolute;
    width: 70%;
    top: 30%;
    left: 0%;

    @media ${props => props.theme.mobileDown} {
      width: 60%;
      top: 30%;
      left: 20%;
    }
  }

  &:nth-of-type(4) {
    position: absolute;
    width: 60%;
    top: 50%;
    left: -10%;

    @media ${props => props.theme.mobileDown} {
      width: 50%;
      top: 50%;
      left: 54%;
    }
  }
`

const El = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  padding-top: ${rem(15)};
  z-index: 10;
`

const StyledContainer = styled(Container)`
  position: relative;
  height: 100%;
  z-index: 5;
`

const Logo = styled(motion.div)`
  position: absolute;
  left: ${rem(20)};
  right: ${rem(20)};
  top: ${rem(15)};
  color: #c6c5c6;

  ${props =>
    props.logocolor &&
    css`
      color: ${props => props.logocolor};
    `}

  svg {
    display: block;
    width: ${rem(600)};
    margin: 0 auto;

    transition: color 0.75s, width 0.5s;

    ${props =>
      props.loaded &&
      css`
        width: 100%;
      `}
  }
`

const variants = {
  beforeload: {
    opacity: 0,
    top: "50%",
    // left: '50%',
    // width: '600px',
    y: "-50%",
    // x: '-50%'
  },
  initial: {
    opacity: 1,
    top: "50%",
    // left: '50%',
    // width: '600px',
    y: "-50%",
    // x: '-50%'
  },
  loaded: {
    opacity: 1,
    top: 0,
    // width: 'calc(100% - 40px)',
    y: 0,
    // x: 0,
    // left: '20px',
    // right: '20px'
  },
}

const PreloadImages = props => {
  const loadedContext = useContext(LoadedContext)

  const [imagesLoaded, setImagesLoaded] = useState(1)

  const [loaderState, setLoaderState] = useState(
    !loadedContext.loaded ? "beforeload" : "loaded"
  )

  const images = [
    props.data.loaderImg1,
    props.data.loaderImg2,
    props.data.loaderImg3,
    props.data.loaderImg4,
  ]

  return (
    <AnimatePresence>
      {loadedContext.loaded !== true && (
        <El
          key="preloader"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0 }}
        >
          <StyledContainer>
            <Logo
              loaded={loaderState === "loaded"}
              variants={variants}
              initial={false}
              animate={loaderState}
              transition={{
                duration: 0.5,
                opacity: { duration: 0, delay: 0.2 },
              }}
              onAnimationComplete={() => {
                if (loaderState === "initial") {
                  window.setTimeout(() => {
                    setLoaderState("loaded")
                  }, 700)
                }

                if (loaderState === "loaded") {
                  // props.setLoaded(true)
                  loadedContext.setLoaded(true)

                  // setAnimationEnd(true)
                }
              }}
            >
              <NavLogo />
            </Logo>
          </StyledContainer>

          <Images>
            {images.map((item, index) => (
              <SingleImage
                key={index}
                initial={{ opacity: 0 }}
                animate={
                  imagesLoaded === images.length
                    ? { opacity: 1 }
                    : loaderState === "loaded" && { y: "-100px", opacity: 0 }
                }
                // transition={{ duration: 0, delay: 0.2*index }}
                transition={
                  loaderState === "beforeload"
                    ? { duration: 0, delay: 0.2 * index }
                    : { duration: 0.3, delay: 0.1 * index }
                }
                onAnimationComplete={() => {
                  if (
                    loaderState === "beforeload" &&
                    index + 1 === images.length
                  ) {
                    setLoaderState("initial")
                  }
                }}
              >
                <Img
                  fadeIn={false}
                  fluid={item.childImageSharp.fluid}
                  onLoad={() => {
                    setImagesLoaded(imagesLoaded + 1)
                  }}
                />
              </SingleImage>
            ))}
          </Images>
        </El>
      )}
    </AnimatePresence>
  )
}

export default props => (
  <StaticQuery
    query={graphql`
      query {
        loaderImg1: file(relativePath: { eq: "images/project-1-img-1.jpg" }) {
          childImageSharp {
            fluid(maxWidth: 300, quality: 75) {
              ...GatsbyImageSharpFluid_withWebp_noBase64
            }
          }
        }

        loaderImg2: file(relativePath: { eq: "images/project-1-img-2.jpg" }) {
          childImageSharp {
            fluid(maxWidth: 262, quality: 75) {
              ...GatsbyImageSharpFluid_withWebp_noBase64
            }
          }
        }

        loaderImg3: file(relativePath: { eq: "images/project-1-img-3.jpg" }) {
          childImageSharp {
            fluid(maxWidth: 500, quality: 75) {
              ...GatsbyImageSharpFluid_withWebp_noBase64
            }
          }
        }

        loaderImg4: file(relativePath: { eq: "images/project-1-img-4.jpg" }) {
          childImageSharp {
            fluid(maxWidth: 400, quality: 75) {
              ...GatsbyImageSharpFluid_withWebp_noBase64
            }
          }
        }
      }
    `}
    render={data => <PreloadImages data={data} {...props} />}
  />
)
